/**
 * READ-TIME verdict resolution for the client surfaces that render a STORED receipt.
 *
 * This file holds NO deadline arithmetic and never will. It is a two-function
 * adapter over `resolveStoredVerdict` in `packages/core/src/verification/verdict-law.ts`
 * — the one engine that knows where a stored verdict keeps its deadline — and it
 * exists for exactly one reason: the four client surfaces below each render a
 * receipt in several places, and without a single per-surface projection each of
 * those places grows its own read. Five divergent per-consumer folds is the defect
 * `verdict-law.ts` was written to have ended; five divergent per-RENDER-SITE reads
 * is the same defect one level down.
 *
 * ── Why a stored PENDING is the problem ──
 *
 * A verdict receipt is SIGNED and append-only: `aggregateStatus` sits inside the
 * payload bound by `payloadDigest` and the signature, and
 * `verification_protocol_objects` is INSERT-OR-IGNORE with no UPDATE anywhere. So
 * the stored bytes keep saying PENDING forever, and a surface that renders
 * `receipt.payload.aggregateStatus` shows a person "PENDING" — "still waiting" —
 * about a deadline that passed weeks ago. Lane WIRE wired seven readers through the
 * law; these four client surfaces and the learning-proposal mint were left, with no
 * gate that could refuse a regression in them. `scripts/test-verdict-read-time-surfaces.mjs`
 * is that gate.
 *
 * ── `now` is INJECTED, never ambient ──
 *
 * Every caller passes a clock read ONCE at its own render edge — a `now` prop with
 * a `new Date().toISOString()` default, the shape `verifier-home.tsx` and
 * `home-page.tsx` already use. A fresh `new Date()` deep inside a render is the
 * defect, not the fix: it makes the surface untestable (no test can stand on either
 * side of a deadline) and it lets two render sites on one screen disagree about what
 * time it is. The gate enforces this structurally — it parses each surface and
 * refuses a call whose `now` argument is a literal rather than the edge binding.
 *
 * ── Expiry can never yield PASS ──
 *
 * Not asserted here, because it is not this file's to assert: `PendingResolution` is
 * `Exclude<VerificationStatus, "PASS" | "PENDING">`, so a resolution that expires
 * into success FAILS TO COMPILE in `verdict-law.ts`, and `resolveStoredVerdict`
 * keeps a runtime throw behind that for the built dist. Both are one level down, and
 * re-asserting them here would only add a second place to weaken.
 */
import { resolveStoredVerdict } from "@excellent/core/verification-verdict-law";

/**
 * The shape every client receipt satisfies: a signed envelope around a payload that
 * carries an id and a verdict.
 *
 * Structurally typed rather than imported from any one surface's DTO, because the
 * four callers reach four different generated contract types that agree on exactly
 * this much. `determinations` is where the deadline actually lives and is passed
 * straight through to the law — this file never inspects it.
 */
export type RenderableReceipt = {
  readonly payload: Parameters<typeof resolveStoredVerdict>[0]["verdict"] & {
    readonly id: string;
  };
};

/** The status a person must be shown for ONE stored receipt, read at `now`. */
export function resolvedVerdictOf(receipt: RenderableReceipt, now: string): string {
  return resolveStoredVerdict({ verdict: receipt.payload, now }).status;
}

/**
 * The status a person must be shown for EVERY receipt on a surface, read at ONE
 * instant, keyed by receipt id.
 *
 * Built once per surface (under `useMemo`) and read per row, rather than resolved
 * per row: a surface that resolved inside its row renderer would read the clock N
 * times and could render two rows against two different instants — a table whose
 * top half has expired and whose bottom half has not.
 *
 * Callers read it through {@link verdictFrom} so that a receipt missing from the map
 * can never silently render as the empty string.
 */
export function resolveReceiptVerdicts(
  receipts: readonly RenderableReceipt[],
  now: string,
): ReadonlyMap<string, string> {
  const resolved = new Map<string, string>();
  for (const receipt of receipts) {
    resolved.set(receipt.payload.id, resolvedVerdictOf(receipt, now));
  }
  return resolved;
}

/**
 * Read one receipt's resolved verdict out of a surface's map.
 *
 * A receipt the surface did not include in its projection falls back to resolving
 * that ONE receipt at the same `now` — never to `receipt.payload.aggregateStatus`,
 * which is the stale read this whole file exists to remove, and never to `""`, which
 * would render a blank badge where a verdict belongs. The fallback keeps the map an
 * optimization rather than a correctness precondition.
 */
export function verdictFrom(
  resolved: ReadonlyMap<string, string>,
  receipt: RenderableReceipt,
  now: string,
): string {
  return resolved.get(receipt.payload.id) ?? resolvedVerdictOf(receipt, now);
}
